import React, { useEffect, useState, useMemo, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import Dropdown from "../components/Dropdown.js";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, TrendingUp } from "lucide-react";
import { ThemeContext } from "../ThemeContext";


// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border border-white/20 dark:border-gray-700/50 ${className}`}
      style={{
        boxShadow: isHovered
          ? '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 40px rgba(0, 0, 0, 0.1)'
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
      }}
    >
      {children}
    </motion.div>
  );
};

const CardHeader = ({ title, icon: Icon, isDarkMode, children }) => {
  const sectionHeadingGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  return (
    <div className="flex justify-between items-center mb-6 gap-4">
      <div className="flex items-center gap-3">
        {Icon && (
          <motion.div
            whileHover={{ rotate: 360 }}
            transition={{ duration: 0.6 }}
            className="p-2 rounded-xl bg-[#a6a6a6] text-white"
          >
            <Icon size={20} />
          </motion.div>
        )}
        <h3 
          key={`${title}-${isDarkMode}`}
          className="font-bold text-xl sm:text-2xl md:text-3xl font-zonaProUltra relative inline-block"
          style={{
            color: isDarkMode ? '#ff7a00' : '#212D82',
            background: sectionHeadingGradient,
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'shine 10s linear infinite',
            lineHeight: '1.2',
            paddingBottom: '0.25rem'
          }}
        >
          {title}
        </h3>
        {title === "Weekly Score" && (
          <span className="text-[10px] sm:text-xs md:text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular">(Sun - Today)</span>
        )}
      </div>
      {children && (
        <div className="flex-shrink-0">
          {children}
        </div>
      )}
    </div>
  );
};

const LeaderboardTable = ({ title, categories, selectedCategory, onCategoryChange, leaderboardData, currentUserId, delay = 0, isDarkMode }) => {
  const processedLeaderboardData = useMemo(() => {
    if (!leaderboardData || leaderboardData.length === 0) return [];

    return leaderboardData.map((entry) => ({
      ...entry,
      rank: entry.rank,
      isCurrentUser: String(entry.user_id) === String(currentUserId),
    }));
  }, [leaderboardData, currentUserId]);


  const displayData = [
    ...processedLeaderboardData
      .filter(entry => entry.rank <= 10)
      .sort((a, b) => a.rank - b.rank),
    ...processedLeaderboardData.filter(entry => entry.isCurrentUser && entry.rank > 10)
  ];

  return (
    <Card className="flex flex-col w-full" delay={delay}>
      <CardHeader 
        title={title} 
        icon={title === "Total Score" ? Trophy : TrendingUp}
        isDarkMode={isDarkMode}
      >
        <Dropdown 
          options={categories.map(c => c.name)}
          selectedOption={selectedCategory}
          onOptionChange={onCategoryChange}
        />
      </CardHeader>
      <div className="overflow-y-auto max-h-[600px]">
        {displayData.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-500 dark:text-gray-400 font-semibold font-zonaProRegular">No data available</p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2 items-center text-xs font-bold text-gray-400 dark:text-gray-400 uppercase pb-3 border-b border-gray-200 dark:border-gray-700 px-2">
              <span className="font-zonaProRegular text-sm dark:text-gray-300">Rank</span>
              <span className="font-zonaProRegular text-sm dark:text-gray-300">Name</span>
              <span className="font-zonaProRegular text-sm dark:text-gray-300">Score</span>
            </div>
            <AnimatePresence>
              {displayData.map((entry, index) => {
                const { rank, user_name, score, isCurrentUser } = entry;
                const isTopThree = rank <= 3;
                
                const textClass = isTopThree ? "font-zonaProUltra font-bold text-xl" : "font-zonaProRegular text-base";
                
                let rankColorStyle = {};
                let rankDisplay = rank;

                if (rank === 1) {
                  rankColorStyle = { color: '#eab308' }; // yellow-500 (bright yellow)
                  rankDisplay = '🥇';
                } else if (rank === 2) {
                  rankColorStyle = { color: '#9ca3af' }; // gray-400 (medium gray)
                  rankDisplay = '🥈';
                } else if (rank === 3) {
                  rankColorStyle = { color: '#d97706' }; // amber-600 (orange/bronze)
                  rankDisplay = '🥉';
                } else {
                  rankColorStyle = { color: isDarkMode ? '#9ca3af' : '#4b5563' }; // gray-400 for dark, gray-600 for light
                }

                return (
                  <motion.div
                    key={entry.user_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05, duration: 0.4 }}
                    whileHover={{ x: 5, scale: 1.02 }}
                    className={`grid grid-cols-3 gap-2 items-center p-3 rounded-lg transition-all ${
                      isCurrentUser 
                        ? 'bg-cyan-100 dark:bg-cyan-900/30 ring-2 ring-cyan-400 dark:ring-cyan-600' 
                        : 'hover:bg-gray-100/50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <motion.span
                      className={`font-bold ${textClass}`}
                      style={rankColorStyle}
                      animate={isTopThree ? {
                        scale: [1, 1.1, 1],
                      } : {}}
                      transition={isTopThree ? {
                        duration: 1,
                        repeat: Infinity,
                        delay: index * 0.2,
                      } : {}}
                    >
                      {rankDisplay}
                    </motion.span>
                    <span className={`truncate ${textClass} text-gray-800 dark:text-gray-200`}>{user_name}</span>
                    <motion.span
                      className={`text-right font-bold ${textClass}`}
                      style={title === "Total Score" ? { color: '#ff7a00' } : { color: '#84cc16' }}
                      initial={{ opacity: 0 }}
                      animate={{ 
                        opacity: 1,
                        scale: [1, 1.1, 1],
                      }}
                      transition={{ 
                        opacity: { delay: index * 0.05 + 0.2 },
                        scale: {
                          duration: 1,
                          repeat: Infinity,
                          delay: index * 0.2,
                        }
                      }}
                    >
                      {score.toLocaleString()}
                    </motion.span>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </Card>
  );
};

const LeaderboardPage = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const [totalLeaderboard, setTotalLeaderboard] = useState([]);
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState([]);

  const [totalCategories, setTotalCategories] = useState([]);
  const [weeklyCategories, setWeeklyCategories] = useState([]);

  const [selectedTotalCategory, setSelectedTotalCategory] = useState("");
  const [selectedWeeklyCategory, setSelectedWeeklyCategory] = useState("");

  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("sat_user"));
  const user_id_unparsed = user ? user.user_id : null;

  // safer: remove ALL dashes
  const currentUserId = user_id_unparsed
    ? user_id_unparsed.toString().replace(/-/g, "")
    : null;

  const isPremium = user ? user.is_premium === true : false;
  const trialUsed = user ? user.trial_used === true : false;

  const titleGradient = useMemo(() => {
    const color = isDarkMode ? "#ff7a00" : "#212D82";
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  const formatCategoryName = (name) =>
    name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const fetchTotalLeaderboard = (rawCategory) => {
    return api
      .get(`/api/leaderboard/?type=total&category=${encodeURIComponent(rawCategory)}&user_id=${currentUserId}`)
      .then((response) => {
        const { topPlayers, currentPlayer } = response.data;

        let finalLeaderboard = [...(topPlayers || [])];
        if (
          currentPlayer &&
          !finalLeaderboard.some((p) => p.user_id === currentPlayer.user_id)
        ) {
          finalLeaderboard.push(currentPlayer);
        }

        setTotalLeaderboard(finalLeaderboard);
      })
      .catch((error) => {
        console.error("There was an error fetching the leaderboard!", error);
      });
  };

  const fetchWeeklyLeaderboard = (rawCategory) => {
    return api
      .get(
        `/api/weekly-leaderboard/?user_id=${currentUserId}&category=${encodeURIComponent(
          rawCategory
        )}`
      )
      .then((res) => {
        const { topPlayers, currentPlayer, categories } = res.data;

        const weeklyCats = [
          { name: "Total Score", rawName: "Total Score" },
          ...(categories || []).map((c) => ({
            name: formatCategoryName(c),
            rawName: c,
          })),
        ];

setWeeklyCategories((prev) => (prev.length ? prev : weeklyCats));

        let finalLeaderboard = [...(topPlayers || [])];
        if (
          currentPlayer &&
          !finalLeaderboard.some((p) => p.user_id === currentPlayer.user_id)
        ) {
          finalLeaderboard.push(currentPlayer);
        }

        setWeeklyLeaderboard(finalLeaderboard);
      })
      .catch((error) => console.error("Error fetching weekly leaderboard:", error));
  };

  useEffect(() => {
    api
      .get("/api/total_score_categories/")
      .then((response) => {
        const cats = response.data.map((category) => ({
          name: formatCategoryName(category),
          rawName: category,
        }));
        setTotalCategories(cats);

        if (cats.length > 0) {
          setSelectedTotalCategory(cats[0].name);
          fetchTotalLeaderboard(cats[0].rawName);
        }
      })
      .catch((error) =>
        console.error("Error fetching total score categories:", error)
      );

    // initial weekly load
    setSelectedWeeklyCategory("Total Score");
    fetchWeeklyLeaderboard("Total Score");
  }, []); 

  const handleTotalCategoryChange = (newCategoryName) => {
    setSelectedTotalCategory(newCategoryName);
    const rawName = totalCategories.find((c) => c.name === newCategoryName)?.rawName;
    if (rawName) fetchTotalLeaderboard(rawName);
  };

  const handleWeeklyCategoryChange = (newCategoryName) => {
    setSelectedWeeklyCategory(newCategoryName);
    const rawName = weeklyCategories.find((c) => c.name === newCategoryName)?.rawName;
    if (rawName) fetchWeeklyLeaderboard(rawName);
  };

  return (
    <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Main Content */}
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6">
        {/* Animated Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8"
        >
          <h1
            key={`title-${isDarkMode}`}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Leaderboard
          </h1>
          <motion.p
            className="text-gray-600 dark:text-gray-400 text-lg font-zonaProRegular"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Compete with the best and climb the ranks
          </motion.p>
        </motion.div>

        {/* Leaderboard Tables */}
        <div className="w-full max-w-7xl mx-auto grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8 relative">
          <LeaderboardTable
            title="Total Score"
            categories={totalCategories}
            selectedCategory={
              selectedTotalCategory || (totalCategories[0]?.name ?? "Loading...")
            }
            onCategoryChange={handleTotalCategoryChange}
            leaderboardData={totalLeaderboard}
            currentUserId={currentUserId}
            delay={0.2}
            isDarkMode={isDarkMode}
          />

          <LeaderboardTable
            title="Weekly Score"
            categories={weeklyCategories}
            selectedCategory={selectedWeeklyCategory || "Total Score"}
            onCategoryChange={handleWeeklyCategoryChange}
            leaderboardData={weeklyLeaderboard}
            currentUserId={currentUserId}
            delay={0.4}
            isDarkMode={isDarkMode}
          />
          
          {!isPremium && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-40 flex items-center justify-center backdrop-blur-none bg-white/30 dark:bg-gray-900/30 rounded-3xl"
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-3xl p-8 text-center shadow-2xl border border-white/20 dark:border-gray-700/50 max-w-md"
                >
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="inline-block mb-4"
                  >
                    <Trophy className="w-12 h-12 text-yellow-400" />
                  </motion.div>
                  <h3 
                    key={`premium-${isDarkMode}`}
                    className="text-2xl font-zonaProUltra relative inline-block mb-2"
                    style={{
                      color: isDarkMode ? '#ff7a00' : '#212D82',
                      background: titleGradient,
                      backgroundSize: '200% 100%',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      animation: 'shine 10s linear infinite',
                      lineHeight: '1.2',
                      paddingBottom: '0.25rem'
                    }}
                  >
                    Subscription Required
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 font-zonaProRegular">
                    {trialUsed 
                      ? 'Subscribe to view the full leaderboard and track your progress.'
                      : 'Start your 7-day free trial to access the full leaderboard and track your progress.'}
                  </p>
                  <motion.button
                    onClick={() => navigate('/subscription')}
                    className="py-3 px-8 bg-[#34c759] text-white text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {trialUsed ? 'Subscribe' : 'Start Free Trial'}
                  </motion.button>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
